import type { ColumnConfig, TableTheme } from "../types";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from "./ui/table";

function renderSubRowsCell<T>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subRow: any,
  column: ColumnConfig<T>,
  colIndex: number
) {
  // First column with └ symbol and task name
  if (colIndex === 0 && subRow.task) {
    return (
      <div className="flex items-center">
        <div className="w-6 text-gray-400">└</div>
        <div className="font-medium text-gray-700">{subRow.task.name}</div>
      </div>
    );
  }

  // Use custom cell renderer if provided
  if (column.cell) {
    return column.cell({
      getValue: () => subRow[column.accessorKey || ""],
      row: { original: subRow },
    });
  }

  // Function accessor
  if (typeof column.accessorFn === "function") {
    return column.accessorFn(subRow);
  }

  // Date formatting for accessorKey
  if (column.accessorKey) {
    const value = subRow[column.accessorKey];
    if (column.header.includes("Date") && value && typeof value === "string") {
      try {
        return new Date(value).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        return value;
      }
    }
    return value || "-";
  }

  return "-";
}

export function SubRowsTable<T>({
  subRows,
  subColumns,
  parentIndex,
  theme,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subRows: any[];
  subColumns: ColumnConfig<T>[];
  parentIndex: number;
  theme: TableTheme;
}) {
  return (
    <div className="border-l-2 ml-4 pl-4">
      <Table>
        <TableHeader>
          <TableRow>
            {subColumns.map((column, colIndex) => (
              <TableHead
                key={colIndex}
                className={`${theme.headerBg} ${theme.headerText} text-xs py-2`}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {subRows.map((subRow, subIndex) => (
            <TableRow
              key={`${parentIndex}-sub-${subIndex}`}
              className={`border-b border-gray-100 ${theme.subRowOddBg} even:${theme.subRowEvenBg} ${theme.subRowHover} ${theme.subRowText}`}
            >
              {subColumns.map((column, colIndex) => (
                <TableCell
                  key={`${parentIndex}-sub-${subIndex}-col-${colIndex}`}
                  className="py-2"
                >
                  {renderSubRowsCell(subRow, column, colIndex)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
